/* @flow */
/**
 * Field: part of an application can be added in a flow code block.
 * The types contained in this file should not be changed lightly as they are stored on the CMR (kwanza) and user by other software (e.g. Manatee) which expects them to look a certain way.
 * @typedef { Object } Field
 * @memberof FieldService
 */
import _ from 'lodash'
import Typenames from './Typenames'
import { ActionTypes, store } from 'store'
import { User } from 'services'

type Offset = {
  x?: number,
  y?: number
}

export type FieldAlternative = {
  path: string,
  matchconfidence?: number,
  image?: string,
  offset?: Offset,
  matchondesktop?: boolean,
  includeInvisible?: boolean,
  type?: string,
  traversalArgs?: Array<'STEPPER'>
}

export type Field = {
  id?: string,
  path: string,
  pathInfos?: Array<Object>,
  name: string,
  version?: string,
  actions: Array<'CLICK' | 'INPUT' | 'READ' | 'FOCUS'>,
  application: string,
  matchconfidence?: number,
  image?: string,
  offset?: Offset,
  matchondesktop?: boolean,
  includeInvisible?: boolean,
  type?: string,
  alternatives?: Array<FieldAlternative>,
  traversalArgs?: Array<'STEPPER'>
}

/**
 * Field typename
 * @private
 * @memberof FieldService
 */
const TYPENAME = Typenames.FIELD

/**
 * Validates a passed object against the required Field type properties
 * @function
 * @memberof FieldService
 *
 * @return { boolean } The information if the passed object contains all the required Field properties
 */
const validateFieldObject = (field?: Object): boolean => {
  return ['path', 'name', 'actions', 'application'].every(k => _.has(field, k))
}

/**
 * Creates a new field.
 * @function
 * @memberof FieldService
 *
 * @return { Promise<string> } The id of the added field.
 */
const createField = (field: Field): Promise<PostResponse> => {
  return store.dispatch(ActionTypes.CREATE, { typename: TYPENAME, item: field }).then(r => {
    User.addEdit({
      type: TYPENAME,
      id: r.id,
      name: field.name,
      routeParams: { fieldId: field.id, appId: field.application }
    })
    return r
  })
}

/**
 * Reads a field, flattening 'descriptive'.
 * @function
 * @memberof FieldService
 *
 * @return { Promise<Field> } The field.
 */
const readField = (id: string): Promise<Field> => {
  return store.dispatch(ActionTypes.GET_BY_ID, { typename: TYPENAME, id: id })
}

/**
 * Updates a field.
 * @function
 * @memberof FieldService
 *
 * @return { Promise<any> }
 */
const updateField = (id: string, field: Field): Promise<PostResponse> => {
  return store.dispatch(ActionTypes.UPDATE, { typename: TYPENAME, id: id, item: field }).then(r => {
    User.addEdit({
      type: TYPENAME,
      id,
      name: field.name,
      routeParams: { fieldId: id, appId: field.application }
    })
    return r
  })
}

/**
 * Deletes a field.
 * @function
 * @memberof FieldService
 *
 * @return { Promise<any> }
 */
const deleteField = (id: string): Promise<any> => {
  return store.dispatch(ActionTypes.DELETE, { typename: TYPENAME, id: id })
}

/**
 * Reads all fields for a given app flattening descriptive.
 * @function
 * @memberof FieldService
 *
 * @return { Observable<Array<Field>> } All the fields.
 */
const readFieldsForApp = (appId: string): Promise<Array<Field>> => {
  return store.dispatch(ActionTypes.GET_FOR_APP, { typename: TYPENAME, appId: appId })
}

/**
 * Reads all fields using CachingHttp, flattening descriptive.
 * @function
 * @memberof FieldService
 *
 * @return { Observable<Array<Field>> } All the fields.
 */
const readFields = (): Promise<Array<Field>> => {
  return store.dispatch(ActionTypes.GET, TYPENAME)
}
/**
 * Copies a field.
 * Can be copied to a new App id (optional).
 * @function
 * @memberof FieldService
 *
 * @return { Promise<PostResponse> }
 */
const copyField = (id: string, nameSuffix: string, newAppId?: string): Promise<PostResponse> => {
  return self.readField(id).then(field => {
    const fieldName = `${field.name}${nameSuffix}`
    const fieldToCopy = {
      ...field,
      application: newAppId || field.application,
      name: fieldName
    }
    delete fieldToCopy.id

    return self.createField(fieldToCopy)
  })
}

/**
 * Copies an array of fieldsto a new app id.
 *
 * @function
 * @memberof FieldService
 *
 * @return { Promise<Array<PostResponse>> }
 */
const copyFields = (appId: string, fields: Array<Field>): Promise<Array<PostResponse>> => {
  return Promise.all(fields.map(field => copyField(field.id || '', '', appId)))
}

const self = {
  createField,
  readField,
  updateField,
  deleteField,
  readFieldsForApp,
  readFields,
  copyField,
  copyFields,
  validateFieldObject
}

/**
 * Field service: CRUD interface for fields.
 * @module FieldService
 * @namespace FieldService
 * @version 1.0.0
 */
export default self
